import { useNavigate, useLocation } from "react-router-dom";
import { Navigation2, X, Pause } from "lucide-react";
import { useMinimizedNav, navigationMinimizeStore } from "@/stores/navigationMinimizeStore";

/**
 * Slim global banner that appears at the very top of every page (under the
 * device safe area) whenever the user has minimized an active Vantastika
 * navigation session. Tapping the banner re-opens the full navigation view.
 *
 * Hidden automatically when:
 *  - No navigation is active
 *  - The user is already on /navigate/* (the full UI is showing)
 */
const MinimizedNavBanner = () => {
  const nav = useMinimizedNav();
  const navigate = useNavigate();
  const location = useLocation();

  // Don't show on the navigation page itself.
  if (!nav.active || !nav.tripId) return null;
  if (location.pathname.startsWith("/navigate/")) return null;

  const handleResume = () => {
    if (!nav.tripId) return;
    // Mark as no longer minimized so NavigatePage shows itself again.
    navigationMinimizeStore.set({ active: false });
    navigate(`/navigate/${nav.tripId}`);
  };

  const handleStop = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigationMinimizeStore.clear();
  };

  return (
    <div
      className="fixed left-0 right-0 z-[58] pt-[env(safe-area-inset-top)] pointer-events-none"
      style={{ top: 0 }}
    >
      <div className="px-2 pt-2 pointer-events-auto">
        <button
          type="button"
          onClick={handleResume}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-2xl bg-primary text-primary-foreground shadow-elegant border border-primary-foreground/10 backdrop-blur-xl hover:opacity-95 active:scale-[0.99] transition-all"
          aria-label="Återgå till navigation"
        >
          {/* Maneuver icon */}
          <div className="text-2xl shrink-0 leading-none">
            {nav.paused ? <Pause className="h-6 w-6" /> : (nav.maneuverIcon || <Navigation2 className="h-5 w-5" />)}
          </div>

          {/* Distance + maneuver */}
          <div className="flex-1 min-w-0 text-left">
            {nav.paused ? (
              <>
                <div className="text-xs font-semibold uppercase tracking-wider opacity-80 leading-tight">
                  Pausad
                </div>
                <div className="text-sm font-bold truncate leading-tight">
                  Tryck för att återgå
                </div>
              </>
            ) : (
              <>
                <div className="flex items-baseline gap-2 leading-tight">
                  {nav.distanceLabel && (
                    <span className="font-display text-lg font-black tabular-nums tracking-tight">
                      {nav.distanceLabel}
                    </span>
                  )}
                  <span className="text-sm font-semibold truncate">
                    {nav.maneuverLabel || "Fortsätt"}
                  </span>
                </div>
                {(nav.street || nav.nextStopName) && (
                  <div className="text-[11px] opacity-85 truncate leading-tight mt-0.5">
                    {nav.street ? `på ${nav.street}` : `→ ${nav.nextStopName}`}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Stop button */}
          <span
            role="button"
            tabIndex={0}
            onClick={handleStop}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleStop(e as any); }}
            className="h-8 w-8 rounded-full bg-primary-foreground/15 hover:bg-primary-foreground/25 flex items-center justify-center shrink-0 transition-colors"
            aria-label="Stäng navigation"
          >
            <X className="h-4 w-4" />
          </span>
        </button>
      </div>
    </div>
  );
};

export default MinimizedNavBanner;
